import { useEffect, useMemo, useState } from "react";
import { ChevronDown, PackageX, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnnouncements } from "@/lib/AnnouncementsContext";
import { useLanguage } from "@/hooks/useLanguage";
import { pickLocalized } from "@/lib/localized";
import type { Announcement } from "@/lib/api";
import { useVariantImagesMap } from "@/lib/variantImages";
import { ProductImage } from "./ProductImage";
import { SelectionCircle } from "./SelectionCircle";
import { AnnouncementColorSwatch } from "./AnnouncementColorPicker";

export type AnnouncementVariantOption = {
  key: string;
  label: string;
  imageVariantId?: number;
  // Current state, shown next to the label so admins see what they're changing.
  announcement?: Announcement | null;
  unavailable?: boolean;
};

export type AnnouncementProductOption = {
  key: string;
  name: string;
  variants: AnnouncementVariantOption[];
};

type VariantAnnouncementModalProps = {
  open: boolean;
  announcementId: string | null;
  // When set, the modal assigns availability instead of an announcement:
  // true = mark the selected variants unavailable, false = available again.
  unavailable?: boolean;
  products: AnnouncementProductOption[];
  saving?: boolean;
  onClose: () => void;
  onSave: (
    selectedVariantKeys: string[],
    announcementId: string | null,
    allVariantKeys: string[]
  ) => Promise<void> | void;
};

function VariantThumbnail({
  variant,
  resolvedImageUrl,
  className,
}: {
  variant: AnnouncementVariantOption;
  resolvedImageUrl?: string | null;
  className: string;
}) {
  return (
    <ProductImage
      src={resolvedImageUrl}
      alt={variant.label}
      className={className}
    />
  );
}

export function VariantAnnouncementModal({
  open,
  announcementId,
  unavailable,
  products,
  saving = false,
  onClose,
  onSave,
}: VariantAnnouncementModalProps) {
  const { t } = useTranslation();
  const { byId } = useAnnouncements();
  const { currentLanguage } = useLanguage();
  const allVariantKeys = useMemo(
    () => products.flatMap(product => product.variants.map(variant => variant.key)),
    [products]
  );
  const imageVariantIds = useMemo(
    () =>
      products.flatMap(product =>
        product.variants.flatMap(variant =>
          variant.imageVariantId === undefined ? [] : [variant.imageVariantId]
        )
      ),
    [products]
  );
  const imageUrls = useVariantImagesMap(imageVariantIds);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [expandedProductKeys, setExpandedProductKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedKeys(new Set(allVariantKeys));
      setExpandedProductKeys(new Set());
    }
    // Reset only when opening. Parent rerenders while saving must not replace
    // the user's partial selection with "all variants" again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, saving]);

  if (!open) return null;

  const announcement = announcementId ? byId.get(announcementId) : null;
  const isAvailabilityMode = unavailable !== undefined;
  const title = isAvailabilityMode
    ? t(unavailable ? "announcementAssignment.markUnavailableTitle" : "announcementAssignment.markAvailableTitle")
    : announcement
      ? t("announcementAssignment.assignTitle", { name: announcement.message })
      : t("announcementAssignment.clearTitle");
  const help = isAvailabilityMode
    ? t(unavailable ? "announcementAssignment.markUnavailableHelp" : "announcementAssignment.markAvailableHelp")
    : t(announcement ? "announcementAssignment.help" : "announcementAssignment.clearHelp");
  const saveLabel =
    !isAvailabilityMode && !announcement
      ? t("announcementAssignment.removeFrom", { count: selectedKeys.size })
      : t("announcementAssignment.save", { count: selectedKeys.size });
  const allSelected = allVariantKeys.length > 0 && selectedKeys.size === allVariantKeys.length;

  const toggleVariant = (key: string) => {
    setSelectedKeys(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleProduct = (product: AnnouncementProductOption) => {
    const productKeys = product.variants.map(variant => variant.key);
    const everySelected = productKeys.every(key => selectedKeys.has(key));
    setSelectedKeys(previous => {
      const next = new Set(previous);
      productKeys.forEach(key => {
        if (everySelected) next.delete(key);
        else next.add(key);
      });
      return next;
    });
  };

  const toggleExpanded = (productKey: string) => {
    setExpandedProductKeys(previous => {
      const next = new Set(previous);
      if (next.has(productKey)) next.delete(productKey);
      else next.add(productKey);
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/45 p-4"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="variant-announcement-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-app-border bg-app-surface text-foreground shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-app-border p-5">
          <div>
            <h2 id="variant-announcement-title" className="text-xl font-bold text-brand">
              {title}
            </h2>
            <p className="mt-1 text-sm text-app-muted">
              {help}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("common.close")}
            disabled={saving}
            onClick={onClose}
            className="rounded-full p-2 text-app-muted hover:bg-app-surface-hover disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-app-border px-5 py-3">
          <span className="text-sm font-medium text-foreground">
            {t("announcementAssignment.selectedVariants", { count: selectedKeys.size })}
          </span>
          <button
            type="button"
            onClick={() => setSelectedKeys(allSelected ? new Set() : new Set(allVariantKeys))}
            className="group flex cursor-pointer items-center gap-2 text-sm font-semibold text-brand"
          >
            <SelectionCircle checked={allSelected} />
            <span className="group-hover:underline">
              {allSelected
                ? t("announcementAssignment.clearAll")
                : t("announcementAssignment.assignAll")}
            </span>
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {products.map(product => {
            const selectedCount = product.variants.filter(variant =>
              selectedKeys.has(variant.key)
            ).length;
            const productAllSelected =
              product.variants.length > 0 && selectedCount === product.variants.length;
            const expanded = expandedProductKeys.has(product.key);
            const firstVariant = product.variants[0];

            return (
              <div key={product.key} className="overflow-hidden rounded-xl border border-app-border bg-app-control">
                <div className="flex items-center gap-3 p-4">
                  <button
                    type="button"
                    aria-label={productAllSelected ? t("announcementAssignment.clearAll") : t("announcementAssignment.assignAll")}
                    aria-pressed={productAllSelected}
                    disabled={product.variants.length === 0}
                    onClick={() => toggleProduct(product)}
                    className="group shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <SelectionCircle checked={productAllSelected} />
                  </button>
                  {firstVariant && (
                    <VariantThumbnail
                      variant={firstVariant}
                      resolvedImageUrl={
                        firstVariant.imageVariantId === undefined
                          ? null
                          : imageUrls[firstVariant.imageVariantId]
                      }
                      className="size-12 shrink-0 rounded-lg border border-app-border bg-white"
                    />
                  )}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => toggleExpanded(product.key)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                  >
                    <span className="min-w-0 flex-1 truncate font-semibold text-foreground">
                      {product.name}
                    </span>
                    <span className="text-xs text-app-muted">
                      {selectedCount}/{product.variants.length}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-app-muted transition-transform duration-200 ease-out ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                    expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-1 border-t border-app-border px-4 py-3">
                      {product.variants.length === 0 ? (
                        <p className="text-sm text-app-muted">
                          {t("announcementAssignment.noVariants")}
                        </p>
                      ) : (
                        product.variants.map(variant => {
                          const checked = selectedKeys.has(variant.key);
                          return (
                            <button
                              type="button"
                              key={variant.key}
                              aria-pressed={checked}
                              onClick={() => toggleVariant(variant.key)}
                              className={`group flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-brand/10 ${
                                checked ? "font-medium text-brand" : "text-foreground"
                              }`}
                            >
                              <SelectionCircle checked={checked} />
                              <VariantThumbnail
                                variant={variant}
                                resolvedImageUrl={
                                  variant.imageVariantId === undefined
                                    ? null
                                    : imageUrls[variant.imageVariantId]
                                }
                                className="size-10 shrink-0 rounded-lg border border-app-border bg-white sm:size-12"
                              />
                              <span className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                              <span className="min-w-0 truncate">{variant.label}</span>
                              <span className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-end sm:gap-1.5">
                                {!variant.announcement && (
                                  <span className="inline-flex max-w-full items-center rounded-full border border-dashed border-app-border px-2.5 py-0.5 text-xs text-app-muted">
                                    <span className="truncate">{t("announcementAssignment.noAnnouncement")}</span>
                                  </span>
                                )}
                                {variant.announcement && (
                                  <span className="inline-flex max-w-full items-center gap-1.5 rounded-full sm:max-w-40 border border-app-border bg-app-surface py-0.5 pr-2.5 pl-1 text-xs font-medium text-foreground">
                                    <AnnouncementColorSwatch
                                      backgroundColor={variant.announcement.background_color}
                                      textColor={variant.announcement.text_color}
                                      size={16}
                                    />
                                    <span className="truncate">
                                      {pickLocalized(
                                        variant.announcement.message,
                                        variant.announcement.message_en,
                                        currentLanguage
                                      )}
                                    </span>
                                  </span>
                                )}
                                {variant.unavailable ? (
                                  <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-black/85 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                                    <PackageX size={12} strokeWidth={2.25} aria-hidden="true" />
                                    <span className="truncate">{t("products.statusUnavailable")}</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex max-w-full items-center rounded-full border border-dashed border-app-border px-2.5 py-0.5 text-xs text-app-muted">
                                    <span className="truncate">{t("products.statusAvailable")}</span>
                                  </span>
                                )}
                              </span>
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 border-t border-app-border p-5">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="btn border border-app-border bg-app-control text-foreground hover:bg-app-control-hover"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={saving || selectedKeys.size === 0}
            onClick={() => onSave([...selectedKeys], announcementId, allVariantKeys)}
            className="btn border-none bg-brand text-white hover:bg-brand/90 disabled:opacity-50"
          >
            {saving ? t("common.saving") : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
